import {
  Button,
  Flex,
  NumberInput,
  Space,
  Title,
  Tooltip,
} from "@mantine/core";
import { toNumber } from "lodash";
import { IterationCcwIcon, RotateCcwIcon } from "lucide-react";
import React, { useState } from "react";
import { notification } from "../../../../../../../utils/notification";

interface SectorRotationProps {
  data: any;
  setData: React.Dispatch<React.SetStateAction<any>>;
  selectedDots: Record<string, any>;
  t: (key: string) => string;
  px?: string;
  selectionSectorType: string;
}

const SectorRotation: React.FC<SectorRotationProps> = ({
  data,
  setData,
  selectedDots,
  t,
  px,
  selectionSectorType,
}) => {
  const [halfRotationDegrees, setHalfRotationDegrees] = useState<number>(25);

  const rotateSeats = (direction: "left" | "right") => {
    // Create a deep copy of the data
    const newData = JSON.parse(JSON.stringify(data));

    // Rotation angle in radians for left and right
    const angle = Math.PI / halfRotationDegrees; // Convert the degrees to radians
    const rotateMatrix = direction === "left" ? angle : -angle;

    let keySearch = ":1:1";
    if (selectionSectorType) {
      let rowIndex = selectedDots[0].split(":")[1];
      keySearch = `${rowIndex}:1`;
    }

    const pivotKey = selectedDots.find((key: string) =>
      key.endsWith(keySearch)
    );

    if (!pivotKey) {
      notification({
        type: "error",
        title: "Could not find pivot key",
      });

      return;
    }

    const pivotSeat = newData.data[pivotKey];

    if (pivotSeat) {
      const pivotX = parseFloat(pivotSeat.rect.x);
      const pivotY = parseFloat(pivotSeat.rect.y);

      if (isNaN(pivotX) || isNaN(pivotY)) return;

      selectedDots.forEach((selectedKey: string) => {
        const seat = newData.data[selectedKey];

        if (seat) {
          let x = parseFloat(seat.rect.x);
          let y = parseFloat(seat.rect.y);

          // Ensure the x and y are valid numbers
          if (isNaN(x) || isNaN(y)) return;

          // Step 1: Translate the seat to the origin (relative to pivot)
          let translatedX = x - pivotX;
          let translatedY = y - pivotY;

          // Step 2: Rotate the seat around the pivot (origin)
          let rotatedX =
            translatedX * Math.cos(rotateMatrix) -
            translatedY * Math.sin(rotateMatrix);
          let rotatedY =
            translatedX * Math.sin(rotateMatrix) +
            translatedY * Math.cos(rotateMatrix);

          // Step 3: Translate the seat back to its original position
          let finalX = rotatedX + pivotX;
          let finalY = rotatedY + pivotY;

          seat.rect.x = finalX.toString();
          seat.rect.y = finalY.toString();
          newData.data[selectedKey] = seat;
        } else {
          notification({
            type: "error",
            title: `Seat ${selectedKey} does not exist in data.data`,
          });
        }
      });

      setData((prev: any) => ({ ...prev, data: newData.data }));
    } else {
      notification({
        type: "error",
        title: "Pivot Seat Not Found",
        message: "The specified pivot seat could not be found.",
      });
    }
  };

  return (
    <>
      <Title px={px} order={6}>
        {t("scheme.redactor.contextMenu.labels.rotationTitle")}
      </Title>
      <Space h="xs" />

      <Flex px={px} gap={10}>
        <Button size="xs" onClick={() => rotateSeats("left")}>
          <IterationCcwIcon />
        </Button>
        <Tooltip
          label={t("scheme.redactor.contextMenu.labels.rotationAngleInRadians")}
        >
          <NumberInput
            size="xs"
            min={1}
            value={halfRotationDegrees}
            onChange={(value) => setHalfRotationDegrees(toNumber(value))}
          />
        </Tooltip>
        <Button size="xs" onClick={() => rotateSeats("right")}>
          <RotateCcwIcon />
        </Button>
      </Flex>
    </>
  );
};

export default React.memo(SectorRotation);
